import { addDays, hashString, normalize, startOfWeek } from './dates';
import { DEFAULT_CRITERIA, generateSchedule } from './generator';
import { NATAL_SAMPLE_HOMES, sampleAddress, sampleLocation } from './geography';
import { legacyWeeklyRule } from './recurrence';
import { NO_ROLE_RULES } from './roleRules';
import { sampleAudiences, sampleVisitors } from './sampleMembership';
import { newMember } from './transitions';
import type {
  AvailState,
  CivilStatus,
  DomainState,
  JoinedVia,
  Member,
  Ministry,
  ScheduleEvent,
  Sex,
} from './types';

/** Quantas pessoas a demonstração tem, contando os administradores que ficam. */
export const CHURCH_DEMO_MEMBERS = 100;

/**
 * Os e-mails (fictícios) dos perfis de teste: quem lidera a Recepção, quem lidera a
 * Projeção e um membro sem liderança. Nenhum recebe conta: para entrar como um deles, o
 * Administrador troca o e-mail por um seu e convida em Acessos.
 */
export const CHURCH_DEMO_PROFILES = {
  leader: 'teste.lider.ministerio@exemplo.com',
  roleLeader: 'teste.lider.funcao@exemplo.com',
  member: 'teste.membro@exemplo.com',
} as const;

/**
 * 5 Ministérios diversos com exatamente 3 funções em cada, cobrindo as áreas essenciais
 * da igreja: Recepção, Louvor, Multimídia, Infantil e Mídia.
 */
export const CHURCH_DEMO_MINISTRIES: readonly Ministry[] = [
  {
    id: 'rec',
    name: 'Recepção e Acolhimento',
    description: 'Acolhimento na entrada, portaria e recepção de membros e visitantes.',
    color: '#2563EB',
    icon: 'door',
    leaderIds: [],
    welcome: true,
    roles: [
      {
        id: 'portaria',
        name: 'Portaria',
        qty: 2,
        allowedSex: 'any',
        rules: ['Chegar 30 min antes'],
        leaderIds: [],
        icon: 'door',
        ...NO_ROLE_RULES,
      },
      {
        id: 'acolhimento',
        name: 'Acolhimento',
        qty: 2,
        allowedSex: 'any',
        rules: [],
        leaderIds: [],
        icon: 'users',
        ...NO_ROLE_RULES,
      },
      {
        id: 'guardioes',
        name: 'Guardiões',
        qty: 2,
        allowedSex: 'any',
        rules: ['Maiores de 18 anos'],
        leaderIds: [],
        icon: 'guard',
        ...NO_ROLE_RULES,
      },
    ],
  },
  {
    id: 'lou',
    name: 'Louvor',
    description: 'Música, instrumental e ministração de adoração nos cultos.',
    color: '#16A34A',
    icon: 'music',
    leaderIds: [],
    welcome: false,
    roles: [
      {
        id: 'vocal',
        name: 'Vocal',
        qty: 2,
        allowedSex: 'any',
        rules: ['Ensaio no sábado'],
        leaderIds: [],
        icon: 'mic',
        ...NO_ROLE_RULES,
      },
      {
        id: 'teclado',
        name: 'Teclado',
        qty: 1,
        allowedSex: 'any',
        rules: [],
        leaderIds: [],
        icon: 'keyboard',
        ...NO_ROLE_RULES,
      },
      {
        id: 'bateria',
        name: 'Bateria',
        qty: 1,
        allowedSex: 'any',
        rules: [],
        leaderIds: [],
        icon: 'drums',
        ...NO_ROLE_RULES,
      },
    ],
  },
  {
    id: 'mul',
    name: 'Multimídia',
    description: 'Operação de som, projeção de letras e transmissão ao vivo.',
    color: '#0891B2',
    icon: 'monitor',
    leaderIds: [],
    welcome: false,
    roles: [
      {
        id: 'projecao',
        name: 'Projeção',
        qty: 1,
        allowedSex: 'any',
        rules: [],
        leaderIds: [],
        icon: 'projector',
        ...NO_ROLE_RULES,
      },
      {
        id: 'som',
        name: 'Mesa de Som',
        qty: 1,
        allowedSex: 'any',
        rules: ['Treinamento concluído'],
        leaderIds: [],
        icon: 'audio',
        ...NO_ROLE_RULES,
      },
      {
        id: 'transmissao',
        name: 'Transmissão',
        qty: 1,
        allowedSex: 'any',
        rules: [],
        leaderIds: [],
        icon: 'live',
        ...NO_ROLE_RULES,
      },
    ],
  },
  {
    id: 'inf',
    name: 'Infantil',
    description: 'Cuidado com crianças, ensino bíblico e berçário nos cultos.',
    color: '#DB2777',
    icon: 'smile',
    leaderIds: [],
    welcome: false,
    roles: [
      {
        id: 'professor',
        name: 'Professor(a)',
        qty: 2,
        allowedSex: 'any',
        rules: ['Somente cultos da manhã'],
        leaderIds: [],
        icon: 'teacher',
        ...NO_ROLE_RULES,
      },
      {
        id: 'bercario',
        name: 'Berçário',
        qty: 1,
        allowedSex: 'F',
        rules: [],
        leaderIds: [],
        icon: 'nursery',
        ...NO_ROLE_RULES,
      },
      {
        id: 'dinamica',
        name: 'Apoio e Dinâmica',
        qty: 2,
        allowedSex: 'any',
        rules: [],
        leaderIds: [],
        icon: 'dynamics',
        ...NO_ROLE_RULES,
      },
    ],
  },
  {
    id: 'mid',
    name: 'Mídia e Comunicação',
    description: 'Fotografia, redes sociais e design de conteúdo da igreja.',
    color: '#7C3AED',
    icon: 'camera',
    leaderIds: [],
    welcome: false,
    roles: [
      {
        id: 'fotografia',
        name: 'Fotografia',
        qty: 1,
        allowedSex: 'any',
        rules: [],
        leaderIds: [],
        icon: 'camera',
        ...NO_ROLE_RULES,
      },
      {
        id: 'social',
        name: 'Social Media',
        qty: 1,
        allowedSex: 'any',
        rules: [],
        leaderIds: [],
        icon: 'social',
        ...NO_ROLE_RULES,
      },
      {
        id: 'design',
        name: 'Design / Criativo',
        qty: 1,
        allowedSex: 'any',
        rules: [],
        leaderIds: [],
        icon: 'designer',
        ...NO_ROLE_RULES,
      },
    ],
  },
];

const MALE_NAMES = [
  'Lucas',
  'Mateus',
  'Gabriel',
  'Felipe',
  'Rodrigo',
  'Gustavo',
  'Leonardo',
  'Henrique',
  'Davi',
  'Samuel',
  'Caio',
  'Victor',
  'Bruno',
  'Thiago',
  'Rafael',
  'Eduardo',
  'Vinicius',
  'André',
  'Alexandre',
  'Daniel',
];

const FEMALE_NAMES = [
  'Beatriz',
  'Juliana',
  'Fernanda',
  'Camila',
  'Mariana',
  'Larissa',
  'Aline',
  'Priscila',
  'Sabrina',
  'Débora',
  'Raquel',
  'Natália',
  'Vanessa',
  'Tatiane',
  'Rebeca',
  'Mônica',
  'Eliane',
  'Patrícia',
  'Letícia',
  'Carolina',
];

const SURNAMES = [
  'Silva',
  'Santos',
  'Oliveira',
  'Souza',
  'Rodrigues',
  'Ferreira',
  'Alves',
  'Pereira',
  'Lima',
  'Gomes',
  'Costa',
  'Ribeiro',
  'Martins',
  'Carvalho',
  'Almeida',
  'Lopes',
  'Soares',
  'Fernandes',
  'Vieira',
  'Barbosa',
  'Rocha',
  'Dias',
  'Nascimento',
  'Andrade',
  'Moreira',
  'Nunes',
  'Marques',
  'Machado',
  'Mendes',
  'Freitas',
];

const PROFESSIONS = [
  'Professor(a)',
  'Enfermeiro(a)',
  'Engenheiro(a)',
  'Advogado(a)',
  'Desenvolvedor(a) de Software',
  'Contador(a)',
  'Designer Gráfico',
  'Médico(a)',
  'Administrador(a)',
  'Arquiteto(a)',
  'Psicólogo(a)',
  'Fisioterapeuta',
  'Farmacêutico(a)',
  'Comerciante',
  'Estudante',
  'Bancário(a)',
  'Analista de Marketing',
  'Nutricionista',
  'Jornalista',
  'Técnico(a) em Edificações',
];

const CIVIL_STATUSES: CivilStatus[] = ['single', 'married', 'married', 'single', 'stableUnion', 'divorced'];
const JOIN_VIAS: JoinedVia[] = ['baptism', 'baptism', 'transfer', 'profession', 'acclamation'];

/** Os ids da demonstração levam o prefixo da igreja: no banco, os ids valem para todas. */
type Prefix = (id: string) => string;

const ALL_AVAILABLE = (): AvailState[] => Array<AvailState>(21).fill('available');

/** As funções de cada ministério (ids locais), por pessoa: cada uma serve em um ou dois. */
function rolesFor(i: number, sex: Sex): Record<string, string[]> {
  const roles: Record<string, string[]> = {};

  switch (i % 5) {
    case 0:
      roles.rec = i % 2 === 0 ? ['portaria', 'acolhimento'] : ['acolhimento', 'guardioes'];
      if (i % 3 === 0) roles.mid = ['social'];
      break;
    case 1:
      roles.lou = [['vocal', 'teclado'], ['vocal'], ['bateria']][i % 3]!;
      if (i % 4 === 0) roles.mul = ['som'];
      break;
    case 2:
      roles.mul = [['projecao', 'som'], ['som', 'transmissao'], ['projecao']][i % 3]!;
      if (i % 3 === 0) roles.rec = ['portaria'];
      break;
    case 3:
      // O berçário é só para mulheres (a regra de sexo da função).
      if (sex === 'F') roles.inf = i % 2 === 0 ? ['professor', 'bercario'] : ['bercario', 'dinamica'];
      else roles.inf = ['professor', 'dinamica'];
      if (i % 3 === 0) roles.rec = ['acolhimento'];
      break;
    default:
      roles.mid = [['fotografia', 'social'], ['social', 'design'], ['fotografia']][i % 3]!;
      if (i % 4 === 0) roles.mul = ['transmissao'];
  }

  return roles;
}

function prefixRoles(roles: Record<string, string[]>, p: Prefix): Record<string, string[]> {
  return Object.fromEntries(Object.entries(roles).map(([ministry, ids]) => [p(ministry), ids.map(p)]));
}

/**
 * As pessoas da demonstração (sem os administradores), todas ativas, com endereço e
 * coordenada em Natal/RN e habilitadas em funções dos 5 ministérios.
 */
function churchPeople(count: number, p: Prefix, taken: Set<string>, today: string, now: number): Member[] {
  const year = Number(today.slice(0, 4));
  const homes = NATAL_SAMPLE_HOMES.filter((h) => h.city === 'Natal');
  const people: Member[] = [];

  for (let i = 1; i <= count; i += 1) {
    const id = p(`m${i}`);
    const h = hashString(`${id}:church`);
    const sex: Sex = i % 2 === 0 ? 'F' : 'M';
    const firstNames = sex === 'F' ? FEMALE_NAMES : MALE_NAMES;

    let name = '';
    for (let attempt = 0; !name || taken.has(normalize(name)); attempt += 1) {
      const first = firstNames[(h + attempt * 7) % firstNames.length]!;
      const s1 = SURNAMES[(h + attempt * 3 + i) % SURNAMES.length]!;
      const s2 = SURNAMES[(h + attempt * 11 + i * 5) % SURNAMES.length]!;
      name = `${first} ${s1 === s2 ? s1 : `${s1} ${s2}`}`;
    }
    taken.add(normalize(name));

    const age = 18 + (h % 52);
    const birthDate = `${year - age}-${String((h % 12) + 1).padStart(2, '0')}-${String(((h >>> 4) % 28) + 1).padStart(2, '0')}`;
    const home = homes[i % homes.length]!;
    const number = String(10 + ((h >>> 3) % 1900));
    const civilStatus = CIVIL_STATUSES[h % CIVIL_STATUSES.length]!;
    const joinedVia = JOIN_VIAS[h % JOIN_VIAS.length]!;
    const memberSince = addDays(today, -(30 + (h % 1200)));
    const surname = name.split(' ').at(-1)!;

    // Domingo de manhã (0), domingo à noite (2) e quarta à noite (11) livres; o resto, aos poucos.
    const availability = Array.from({ length: 21 }, (_, slot): AvailState => {
      if (slot === 0 || slot === 2 || slot === 11) return h % 7 === 0 ? 'unset' : 'available';

      return (h >>> slot) % 4 === 0 ? 'available' : 'unset';
    });

    people.push({
      ...newMember(id, { name, birthDate, sex, roles: prefixRoles(rolesFor(i, sex), p) }),
      email: `${normalize(name).replace(/[^a-z0-9]+/g, '.')}@exemplo.com`,
      phone: `849${String(10_000_000 + (h % 89_999_999))}`,
      churchStatus: 'active',
      civilStatus,
      profession: PROFESSIONS[h % PROFESSIONS.length]!,
      address: sampleAddress(home, number, i % 4 === 0 ? `Apto ${(h % 80) + 101}` : ''),
      location: sampleLocation(home, number, now),
      availability,
      memberSince,
      joinedVia,
      baptismDate: joinedVia === 'baptism' ? memberSince : `${year - 5}-08-20`,
      originChurch: joinedVia === 'transfer' ? 'Primeira Igreja Batista de Natal' : '',
      emergency: {
        name: `${(sex === 'F' ? MALE_NAMES : FEMALE_NAMES)[(h >>> 5) % 20]} ${surname}`,
        relation: civilStatus === 'married' ? 'Cônjuge' : 'Mãe',
        phone: `849${String(10_000_000 + ((h >>> 2) % 89_999_999))}`,
      },
    });
  }

  return people;
}

/** O Administrador de exemplo, quando a igreja não tem nenhum (os testes do domínio). */
function sampleAdmin(p: Prefix, today: string, now: number): Member {
  const home = NATAL_SAMPLE_HOMES[0]!;
  const year = Number(today.slice(0, 4));

  return {
    ...newMember(p('admin'), { name: 'Daniel Oliveira', birthDate: `${year - 38}-05-12`, sex: 'M' }),
    admin: true,
    email: 'admin.org@exemplo.com',
    phone: '84988776655',
    churchStatus: 'active',
    address: sampleAddress(home, '100'),
    location: sampleLocation(home, '100', now),
    memberSince: `${year - 4}-01-15`,
    joinedVia: 'baptism',
  };
}

/**
 * Os ministérios com os líderes: cada ministério é liderado por alguém habilitado nele e a
 * Projeção ganha um Líder de Função (outra pessoa, habilitada na função). Os perfis de
 * teste recebem os e-mails de `CHURCH_DEMO_PROFILES`.
 */
function withLeaders(people: Member[], p: Prefix): { ministries: Ministry[]; people: Member[] } {
  const leading = new Set<string>();
  const pick = (test: (m: Member) => boolean) => {
    const found = people.find((m) => !leading.has(m.id) && test(m));
    if (found) leading.add(found.id);

    return found?.id;
  };
  const leaders = new Map(CHURCH_DEMO_MINISTRIES.map((m) => [m.id, pick((x) => !!x.roles[p(m.id)]?.length)]));
  const roleLeader = pick((x) => !!x.roles[p('mul')]?.includes(p('projecao')));
  const member = pick((x) => !!x.roles[p('rec')]?.includes(p('portaria')));
  const emails = new Map([
    [leaders.get('rec'), CHURCH_DEMO_PROFILES.leader],
    [roleLeader, CHURCH_DEMO_PROFILES.roleLeader],
    [member, CHURCH_DEMO_PROFILES.member],
  ]);

  return {
    ministries: CHURCH_DEMO_MINISTRIES.map((m) => {
      const leader = leaders.get(m.id);

      return {
        ...m,
        id: p(m.id),
        leaderIds: leader ? [leader] : [],
        roles: m.roles.map((r) => ({
          ...r,
          id: p(r.id),
          rules: [...r.rules],
          leaderIds: r.id === 'projecao' && roleLeader ? [roleLeader] : [],
        })),
      };
    }),
    people: people.map((m) => (emails.has(m.id) ? { ...m, email: emails.get(m.id)! } : m)),
  };
}

const SUNDAY_MORNING_NEEDS: Record<string, number> = {
  portaria: 2,
  acolhimento: 2,
  guardioes: 2,
  vocal: 2,
  teclado: 1,
  bateria: 1,
  projecao: 1,
  som: 1,
  transmissao: 1,
  professor: 2,
  bercario: 1,
  dinamica: 2,
  fotografia: 1,
};

const SUNDAY_EVENING_NEEDS: Record<string, number> = {
  portaria: 2,
  acolhimento: 2,
  guardioes: 2,
  vocal: 2,
  teclado: 1,
  bateria: 1,
  projecao: 1,
  som: 1,
  transmissao: 1,
  fotografia: 1,
  social: 1,
  design: 1,
};

const WEDNESDAY_NEEDS: Record<string, number> = {
  portaria: 1,
  acolhimento: 1,
  vocal: 1,
  teclado: 1,
  som: 1,
  projecao: 1,
};

interface SeriesSpec {
  id: string;
  title: string;
  time: string;
  /** Dias depois do domingo da semana. */
  offset: number;
  recurrence: string;
  need: Record<string, number>;
  ministryIds: string[];
}

const CHURCH_SERIES: SeriesSpec[] = [
  {
    id: 'dom-m',
    title: 'Culto de Domingo - Manhã',
    time: '10:00',
    offset: 0,
    recurrence: 'Semanal · Domingo',
    need: SUNDAY_MORNING_NEEDS,
    ministryIds: ['rec', 'lou', 'mul', 'inf', 'mid'],
  },
  {
    id: 'dom-n',
    title: 'Culto de Domingo - Noite',
    time: '18:00',
    offset: 0,
    recurrence: 'Semanal · Domingo',
    need: SUNDAY_EVENING_NEEDS,
    ministryIds: ['rec', 'lou', 'mul', 'mid'],
  },
  {
    id: 'qua',
    title: 'Culto de Quarta - Oração e Palavra',
    time: '19:30',
    offset: 3,
    recurrence: 'Semanal · Quarta',
    need: WEDNESDAY_NEEDS,
    ministryIds: ['rec', 'lou', 'mul'],
  },
];

/** Quantas semanas antes e depois da atual a demonstração cobre. */
const WEEKS_AROUND = 4;

/**
 * Os cultos das 4 semanas passadas, da atual e das 4 próximas, sem escala: domingo de
 * manhã e à noite e quarta, cada série com a regra semanal da primeira data.
 */
function churchEvents(weekStart: string, p: Prefix): ScheduleEvent[] {
  const events = CHURCH_SERIES.flatMap((s) => {
    const first = addDays(weekStart, -WEEKS_AROUND * 7 + s.offset);
    const rule = legacyWeeklyRule(first);
    const seriesId = p(s.id);

    return Array.from({ length: WEEKS_AROUND * 2 + 1 }, (_, w): ScheduleEvent => {
      const date = addDays(first, w * 7);

      return {
        id: `${seriesId}-${date}`,
        title: s.title,
        color: null,
        date,
        time: s.time,
        seriesId,
        recurrence: s.recurrence,
        rule,
        need: Object.fromEntries(Object.entries(s.need).map(([roleId, qty]) => [p(roleId), qty])),
        slots: {},
        ministryIds: s.ministryIds.map(p),
        generated: false,
        published: false,
        edited: false,
        responses: {},
        attendance: {},
        nudgedAt: null,
        recorded: false,
        notices: {},
        swaps: [],
        audience: null,
      };
    });
  });

  return events.sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
}

/** Tira uma pessoa de cada uma das duas primeiras funções: a escala fica com vagas abertas. */
function withOpenSlots(event: ScheduleEvent): ScheduleEvent {
  const open = Object.keys(event.slots).slice(0, 2);

  return {
    ...event,
    slots: Object.fromEntries(
      Object.entries(event.slots).map(([roleId, ids]) => [roleId, open.includes(roleId) ? ids.slice(1) : ids])
    ),
  };
}

/** O check-in dos cultos que passaram e as confirmações dos publicados à frente. */
function withPresence(event: ScheduleEvent, members: Member[], today: string, now: number): ScheduleEvent {
  if (!event.published) return event;

  const entries = Object.entries(event.slots).flatMap(([roleId, ids]) => ids.map((id) => ({ roleId, id })));
  const inEvent = new Set(entries.map((x) => x.id));

  if (event.date < today) {
    const attendance: ScheduleEvent['attendance'] = {};

    for (const { roleId, id } of entries) {
      const absent = hashString(`${event.id}:${id}`) % 10 === 0;
      const substitute = absent
        ? (members.find((m) => !inEvent.has(m.id) && Object.values(m.roles).flat().includes(roleId))?.id ??
          null)
        : null;

      attendance[id] = absent
        ? { status: 'absent', substituteId: substitute }
        : { status: 'present', substituteId: null };
    }

    return { ...event, recorded: true, attendance };
  }

  const responses: ScheduleEvent['responses'] = {};
  for (const { roleId, id } of entries) {
    if (hashString(`${event.id}:${id}`) % 3 === 0) continue;
    responses[id] = { status: 'confirmed', roleId, reason: '', at: now - (hashString(id) % 36) * 3_600_000 };
  }

  return { ...event, responses };
}

/**
 * Um pedido de troca na Portaria do primeiro culto publicado à frente, já aceito pelo
 * colega e esperando o líder.
 */
function withSwap(event: ScheduleEvent, members: Member[], p: Prefix, now: number): ScheduleEvent {
  const fromId = event.slots[p('portaria')]?.[0];
  const inEvent = new Set(Object.values(event.slots).flat());
  const to = members.find(
    (m) => m.id !== fromId && !inEvent.has(m.id) && !!m.roles[p('rec')]?.includes(p('portaria'))
  );

  if (!fromId || !to) return event;

  return {
    ...event,
    swaps: [
      {
        id: `swap-${event.id}-${fromId}`,
        roleId: p('portaria'),
        fromId,
        toId: to.id,
        status: 'leader',
        at: now - 3_600_000,
      },
    ],
  };
}

/** A igreja só com os administradores: nada a perder ao carregar a demonstração. */
export function isChurchEmpty(state: DomainState): boolean {
  return (
    state.members.every((m) => m.admin) &&
    !state.ministries.length &&
    !state.events.length &&
    !state.visitors.length &&
    !state.registrations.length
  );
}

/**
 * A demonstração está carregada: há um culto das séries dela (`{semente}-dom-m`…) ou um
 * dos perfis de teste (`CHURCH_DEMO_PROFILES`). Não olha os ministérios: os sugeridos da
 * organização nova têm os mesmos ids. Editar o que veio da demonstração não apaga o aviso
 * — só limpar a igreja.
 */
export function hasChurchDemo(state: DomainState): boolean {
  const series = CHURCH_SERIES.map((s) => `-${s.id}`);
  const profiles = new Set<string>(Object.values(CHURCH_DEMO_PROFILES));

  return (
    state.events.some((e) => series.some((s) => e.seriesId.endsWith(s))) ||
    state.members.some((m) => profiles.has(m.email))
  );
}

/**
 * Carrega a demonstração numa igreja vazia (`isChurchEmpty`): os administradores ficam com
 * o próprio cadastro (o primeiro ganha funções na Recepção e no Louvor e a disponibilidade
 * toda), e chegam as outras pessoas até `CHURCH_DEMO_MEMBERS` em Natal/RN, 5 ministérios
 * com 3 funções cada (líderes e os perfis de teste de `CHURCH_DEMO_PROFILES`), os cultos
 * semanais de domingo e quarta e as fichas de visitantes. Dos cultos, os que passaram saem
 * publicados, com check-in e público (os dois últimos sem registro); dos próximos, os dois
 * primeiros publicados (com confirmações e um pedido de troca), o terceiro em rascunho
 * completo, o quarto com vagas abertas e os outros sem escala, para a geração. As
 * configurações e os links públicos da igreja não mudam. Os ids levam o `seed` (o banco
 * não aceita o mesmo id em duas igrejas).
 */
export function createChurchDemoState(
  base: DomainState,
  options: { seed: string; today: string; now?: number }
): DomainState {
  const { today } = options;
  const now = options.now ?? Date.now();
  const p: Prefix = (id) => `${options.seed}-${id}`;

  const kept = base.members.filter((m) => m.admin);
  const admins = kept.length ? kept : [sampleAdmin(p, today, now)];
  const [first, ...others] = admins;
  const admin: Member = {
    ...first!,
    roles: { [p('rec')]: [p('acolhimento')], [p('lou')]: [p('vocal')] },
    availability: ALL_AVAILABLE(),
  };
  const taken = new Set(admins.map((m) => normalize(m.name)));
  const led = withLeaders(
    churchPeople(Math.max(CHURCH_DEMO_MEMBERS - admins.length, 0), p, taken, today, now),
    p
  );
  const members = [admin, ...others, ...led.people];
  const { ministries } = led;

  let events = churchEvents(startOfWeek(today), p);
  const future = events.filter((e) => e.date >= today);
  const published = new Set(
    [...events.filter((e) => e.date < today), ...future.slice(0, 2)].map((e) => e.id)
  );
  const partialId = future[3]?.id;
  const toGenerate = events.filter((e) => e.date < today).concat(future.slice(0, 4));

  events = generateSchedule(
    events,
    toGenerate.map((e) => e.id),
    members,
    ministries,
    {
      ministryIds: ministries.map((m) => m.id),
      disabledRoleIds: [],
      limit: base.settings.maxPerMonth,
      allowUnset: true,
      criteria: DEFAULT_CRITERIA,
    }
  ).events.map((e) => {
    const event = { ...e, published: published.has(e.id) };

    return e.id === partialId ? withOpenSlots(event) : withPresence(event, members, today, now);
  });
  events = sampleAudiences(
    events,
    today,
    admins.map((m) => m.name),
    2
  );

  const swapId = events.find((e) => e.published && e.date >= today)?.id;
  events = events.map((e) => (e.id === swapId ? withSwap(e, members, p, now) : e));

  const recLeader = ministries.find((m) => m.id === p('rec'))?.leaderIds[0];
  const sample = sampleVisitors({
    events,
    today,
    ownerIds: [recLeader ?? admin.id, admin.id],
    inviterIds: led.people.slice(0, 15).map((m) => m.id),
    staffName: admin.name,
    idPrefix: p(''),
  });

  return {
    ...base,
    ministries,
    members,
    events,
    log: [],
    readNotifications: [],
    registrations: sample.referral ? [sample.referral] : [],
    privacyRequests: [],
    visitors: sample.visitors,
  };
}

/**
 * Limpa a igreja: saem as pessoas (menos os administradores), os ministérios, os eventos,
 * as fichas de visitantes, os cadastros pelo link, os pedidos de privacidade e o histórico.
 * Os administradores ficam com o próprio cadastro, sem funções (e sem o cônjuge que saiu);
 * as configurações e os links públicos ficam — os QR codes impressos continuam valendo — e
 * os Primeiros passos voltam.
 */
export function clearChurchState(state: DomainState): DomainState {
  const kept = new Set(state.members.filter((m) => m.admin).map((m) => m.id));
  const members = state.members
    .filter((m) => kept.has(m.id))
    .map((m) =>
      m.spouseId && !kept.has(m.spouseId)
        ? { ...m, roles: {}, spouseId: null, coupleMode: null }
        : { ...m, roles: {} }
    );

  return {
    ...state,
    ministries: [],
    members,
    events: [],
    log: [],
    readNotifications: [],
    onboardingHidden: false,
    registrations: [],
    privacyRequests: [],
    visitors: [],
  };
}
